import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle, Users, TrendingUp, Shield, Sparkles, Globe } from 'lucide-react';

export const Synergies = () => {
  const benefits = [
    {
      icon: Sparkles,
      title: 'Une expérience fluide',
      description: 'Tout est connecté, tout est simple',
      color: '#FF6600'
    },
    {
      icon: TrendingUp,
      title: 'Des avantages maximisés',
      description: 'Vos achats se renforcent mutuellement',
      color: '#00A651'
    },
    {
      icon: Shield,
      title: 'Une qualité garantie',
      description: 'Tous nos services répondent aux mêmes standards d\'excellence',
      color: '#003366'
    },
    {
      icon: Users,
      title: 'Un partenaire unique',
      description: 'Pour l\'essentiel de vos besoins quotidiens',
      color: '#FFB81C'
    }
  ];

  const countryBenefits = [
    {
      icon: Users,
      title: 'Création d\'emplois',
      description: 'À travers toute la chaîne de valeur'
    },
    {
      icon: TrendingUp,
      title: 'Développement d\'infrastructures',
      description: 'Modernes et durables'
    },
    {
      icon: Globe,
      title: 'Transfert de technologies',
      description: 'Montée en compétences'
    },
    {
      icon: Shield,
      title: 'Souveraineté énergétique',
      description: 'Via nos infrastructures pétrolières'
    },
    {
      icon: Sparkles,
      title: 'Innovation',
      description: 'Adaptée au contexte africain'
    }
  ];

  const synergies = [
    {
      title: 'Habitat + Énergie',
      description: 'Nos résidences KIDH sont équipées de stations-services Wayne intégrées pour le confort des résidents.',
      color: '#00A651'
    },
    {
      title: 'Énergie + Technologie',
      description: 'Boutiques EKIPTECH dans chaque station Wayne, offrant solutions domotiques et IoT.',
      color: '#FF6600'
    },
    {
      title: 'Technologie + Habitat',
      description: 'Maisons intelligentes KIDH équipées des dernières solutions domotiques EKIPTECH.',
      color: '#003366'
    },
    {
      title: 'KIPAY + Tous',
      description: 'Programme de fidélité unifié offrant des avantages sur tous les services de l\'%u00e9cosystème.',
      color: '#FFB81C'
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#003366] to-[#004080]">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-block mb-6">
            <span className="bg-[#FF6600] text-white px-6 py-2 rounded-full text-sm font-semibold">
              L'Écosystème EKIP
            </span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Quand 1+1+1+1 = <span className="text-[#FFB81C]">10</span>
          </h1>
          <p className="text-2xl text-gray-200 max-w-3xl mx-auto">
            La Puissance de l'Intégration au Service de Votre Quotidien
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xl text-gray-700 leading-relaxed">
            EKIP se distingue par sa capacité à créer des <span className="font-bold text-[#FF6600]">synergies intelligentes</span> entre 
            ses filiales, formant un écosystème complet où chaque élément renforce les autres pour créer une valeur multipliée.
          </p>
        </div>
      </section>

      {/* Synergies Grid */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#003366] mb-6">
              Comment Nos Filiales Travaillent Ensemble
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {synergies.map((synergy, index) => (
              <Card key={index} className="border-2 hover:shadow-xl transition-all duration-300" style={{ borderColor: synergy.color }}>
                <CardContent className="p-8">
                  <div className="flex items-start space-x-4">
                    <div 
                      className="w-2 h-full rounded-full flex-shrink-0"
                      style={{ backgroundColor: synergy.color }}
                    />
                    <div>
                      <h3 className="text-2xl font-bold text-[#003366] mb-4">{synergy.title}</h3>
                      <p className="text-gray-700 leading-relaxed text-lg">{synergy.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Client Benefits */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#003366] mb-6">
              Pour Vous, Client
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Les bénéfices concrets de notre écosystème intégré
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <Card key={index} className="text-center hover:shadow-xl transition-shadow duration-300 border-0 shadow-md">
                  <CardContent className="p-8">
                    <div 
                      className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6"
                      style={{ backgroundColor: `${benefit.color}15` }}
                    >
                      <Icon style={{ color: benefit.color }} size={32} />
                    </div>
                    <div className="flex items-center justify-center mb-3">
                      <CheckCircle className="text-[#00A651] mr-2" size={24} />
                      <h3 className="text-xl font-bold text-[#003366]">{benefit.title}</h3>
                    </div>
                    <p className="text-gray-600">{benefit.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Country Benefits */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#003366] to-[#004080]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Pour la Côte d'Ivoire
            </h2>
            <p className="text-xl text-gray-200 max-w-3xl mx-auto">
              Notre contribution au développement national
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {countryBenefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <Card key={index} className="bg-white/10 backdrop-blur-sm border-white/20 hover:bg-white/20 transition-all duration-300">
                  <CardContent className="p-8">
                    <div className="flex items-start space-x-4">
                      <div className="bg-[#FF6600] p-3 rounded-lg flex-shrink-0">
                        <Icon className="text-white" size={24} />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white mb-2">{benefit.title}</h3>
                        <p className="text-gray-200">{benefit.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Example Scenario */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#003366] mb-6">
              Un Exemple Concret
            </h2>
            <p className="text-xl text-gray-600">
              Découvrez comment l'écosystème EKIP transforme votre quotidien
            </p>
          </div>

          <Card className="border-2 border-[#FF6600] shadow-2xl">
            <CardContent className="p-10">
              <div className="space-y-8">
                <div className="flex items-start space-x-4">
                  <div className="bg-[#00A651] text-white font-bold w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
                    1
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#003366] mb-2">
                      Vous achetez une maison KIDH
                    </h3>
                    <p className="text-gray-700">
                      Une résidence moderne dans l'une de nos cités intégrées
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-[#003366] text-white font-bold w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
                    2
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#003366] mb-2">
                      EKIPTECH équipe votre maison
                    </h3>
                    <p className="text-gray-700">
                      Solutions domotiques, sécurité connectée, optimisation énergétique
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-[#FF6600] text-white font-bold w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
                    3
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#003366] mb-2">
                      Station Wayne à proximité
                    </h3>
                    <p className="text-gray-700">
                      Carburants, produits essentiels, boutique EKIPTECH intégrée
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-[#FFB81C] text-white font-bold w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
                    4
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#003366] mb-2">
                      KIPAY récompense votre fidélité
                    </h3>
                    <p className="text-gray-700">
                      Points cumulés sur tous vos achats, avantages exclusifs, paiements simplifiés
                    </p>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-[#00A651]/10 to-[#00A651]/5 p-6 rounded-lg mt-8">
                  <p className="text-lg text-[#003366] font-semibold text-center">
                    = Une expérience de vie intégrée, fluide et avantageuse ! 
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};